import React, { useState, useEffect, useMemo } from 'react';
import DoctorImageService from '../services/DoctorImageService';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Rating,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Paper,
  Badge,
  Tooltip
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MedicalServices as MedicalIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  School as EducationIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  Psychology as SpecialtyIcon,
  AccessTime as TimeIcon,
  CurrencyRupee as MoneyIcon,
  Refresh as RefreshIcon,
  PhotoCamera as CameraIcon,
  Image as ImageIcon,
  AccountCircle as DefaultAvatarIcon
} from '@mui/icons-material';

// Import Firebase services
import { DashboardService, DoctorManagementService } from '../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';

// Types
interface Doctor {
  id: string;
  name: string;
  credentials: string;
  specialization: string;
  department: string;
  fellowship?: string;
  experience: string;
  rating: number;
  consultationFee: number;
  phone: string;
  email: string;
  availability: string;
  schedule: string[];
  timeSlots: string;
  expertise: string[];
  qualifications: string[];
  avatar: string;
  status: 'Active' | 'On Leave' | 'Inactive';
  statusColor: string;
  todayAppointments?: number;
}

// Firebase service functions for doctors
const FirebaseDoctorService = {
  async getDoctors() {
    try {
      console.log('👩‍⚕️ Fetching doctors from Firebase...');
      const doctors = await DoctorManagementService.getDoctors();
      return {
        success: true,
        data: doctors
      };
    } catch (error) {
      console.error('❌ Error fetching doctors:', error);
      return {
        success: false,
        error: 'Failed to fetch doctors from Firebase',
        data: []
      };
    }
  },

  async addDoctor(doctorData: any) {
    try {
      return await DoctorManagementService.createDoctor(doctorData);
    } catch (error) {
      console.error('❌ Error adding doctor:', error);
      throw error;
    }
  },

  async updateDoctor(doctorId: string, updateData: any) {
    try {
      return await DoctorManagementService.updateDoctor(doctorId, updateData);
    } catch (error) {
      console.error('❌ Error updating doctor:', error);
      throw error;
    }
  },

  async deleteDoctor(doctorId: string) {
    try {
      return await DoctorManagementService.deleteDoctor(doctorId);
    } catch (error) {
      console.error('❌ Error deleting doctor:', error);
      throw error;
    }
  },

  async updateDoctorStatus(doctorId: string, status: string) {
    try {
      return await DoctorManagementService.updateDoctorStatus(doctorId, status.toLowerCase());
    } catch (error) {
      console.error('❌ Error updating doctor status:', error);
      throw error;
    }
  }
};

const mockDepartments = [
  'All',
  'Internal Medicine',
  'Cardiology',
  'Orthopedics',
  'ENT',
  'Neurology',
  'Pediatrics',
  'Gynecology',
  'Surgery',
  'Emergency',
  'Other'
];

const departmentOptionsForForm = [
  'Internal Medicine',
  'Cardiology',
  'Orthopedics',
  'ENT',
  'Neurology',
  'Pediatrics',
  'Gynecology',
  'Surgery',
  'Emergency',
  'Other'
];

const Doctors: React.FC = () => {
  // State management
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  
  // Real-time listener cleanup function
  const [unsubscribe, setUnsubscribe] = useState<(() => void) | null>(null);

  // Load doctors data from Firebase with real-time updates
  useEffect(() => {
    const loadDoctors = async () => {
      try {
        setIsLoading(true);
        console.log('🔄 Setting up real-time doctors data...');
        
        // Set up real-time listener
        const unsubscribeFn = DoctorManagementService.subscribeToDoctor((doctors) => {
          setDoctors(doctors);
          setIsLoading(false);
        });
        
        setUnsubscribe(() => unsubscribeFn);
        
      } catch (error) {
        console.error('❌ Error loading doctors:', error);
        setSnackbar({ 
          open: true, 
          message: 'Failed to load doctors. Please try again.', 
          severity: 'error' 
        });
        setIsLoading(false);
      }
    };

    loadDoctors();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Manual refresh function - resets filters to show all doctors
  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      const result = await FirebaseDoctorService.getDoctors();
      if (result.success) {
        setDoctors(result.data);
        // Reset filters to show all doctors
        setSearchQuery('');
        setSelectedDepartment('All');
        setSnackbar({ 
          open: true, 
          message: 'Doctors data refreshed successfully!', 
          severity: 'success' 
        });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Error refreshing doctors:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to refresh doctors data', 
        severity: 'error' 
      });
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Form state
  const [doctorForm, setDoctorForm] = useState({
    name: '',
    credentials: '',
    specialization: '',
    department: '',
    customDepartment: '',
    fellowship: '',
    experience: '',
    rating: 4.5,
    consultationFee: '',
    phone: '',
    email: '',
    availability: '6 days/week',
    schedule: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    timeSlots: '9:00 AM - 6:00 PM',
    expertise: [''],
    avatar: '',
    profileImage: null as File | null,
    qualifications: [''],
    status: 'Active' as 'Active' | 'On Leave' | 'Inactive'
  });
  
  const [uploadingImage, setUploadingImage] = useState(false);
  const [testingStorage, setTestingStorage] = useState(false);

  // Test storage configuration
  const testStorageConfig = async () => {
    try {
      setTestingStorage(true);
      console.log('🧪 Testing Firebase Storage configuration...');
      console.log('📦 Storage bucket:', storage.app.options.storageBucket);
      console.log('🔑 Project ID:', storage.app.options.projectId);
      
      // Try to get a reference
      const testRef = ref(storage, 'test/connection.txt');
      console.log('✅ Storage reference created successfully');
      
      // Try to list items (this will fail if storage not enabled, but gives better error)
      const { listAll } = await import('firebase/storage');
      const rootRef = ref(storage);
      const result = await listAll(rootRef);
      
      console.log('✅ Storage is working!');
      console.log('📁 Folders:', result.prefixes.map(p => p.name));
      setSnackbar({ 
        open: true, 
        message: 'Storage is configured correctly!', 
        severity: 'success' 
      });
    } catch (error: any) {
      console.error('❌ Storage test failed:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let message = 'Storage error: ';
      if (error.code === 'storage/bucket-not-found') {
        message += 'Storage bucket not found. Go to Firebase Console → Storage → Get Started to enable Storage.';
      } else if (error.code === 'storage/unauthorized') {
        message += 'Permission denied. Make sure you are logged in as admin.';
      } else {
        message += error.message || 'Unknown error';
      }
      
      setSnackbar({ 
        open: true, 
        message, 
        severity: 'error' 
      });
    } finally {
      setTestingStorage(false);
    }
  };

  // Calculate statistics
  const doctorStats = useMemo(() => {
    return DoctorManagementService.calculateStats(doctors);
  }, [doctors]);

  // Filtered doctors
  const filteredDoctors = useMemo(() => {
    return doctors.filter(doctor => {
      const matchesSearch = !searchQuery || 
                           doctor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           doctor.specialization.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           doctor.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           doctor.credentials.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesDepartment = selectedDepartment === 'All' || doctor.department === selectedDepartment;
      
      return matchesSearch && matchesDepartment;
    });
  }, [doctors, searchQuery, selectedDepartment]);

  // Check if doctor is currently available based on schedule and time slots
  const isDoctorCurrentlyAvailable = (doctor: Doctor): boolean => {
    if (doctor.status === 'On Leave' || doctor.status === 'Inactive') {
      return false;
    }

    const now = new Date();
    const currentDay = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][now.getDay()];
    
    // Check if today is in doctor's schedule
    if (!doctor.schedule || !doctor.schedule.includes(currentDay)) {
      return false;
    }

    // Parse time slots (e.g., "9:00 AM - 6:00 PM")
    if (!doctor.timeSlots) return false;
    
    const timeMatch = doctor.timeSlots.match(/(\d{1,2}:\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}:\d{2})\s*(AM|PM)/i);
    if (!timeMatch) return false;

    const [_, startTime, startPeriod, endTime, endPeriod] = timeMatch;
    
    // Convert to 24-hour format
    const parseTime = (time: string, period: string): number => {
      const [hours, minutes] = time.split(':').map(Number);
      let hour24 = hours;
      if (period.toUpperCase() === 'PM' && hours !== 12) hour24 += 12;
      if (period.toUpperCase() === 'AM' && hours === 12) hour24 = 0;
      return hour24 * 60 + minutes;
    };

    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const startMinutes = parseTime(startTime, startPeriod);
    const endMinutes = parseTime(endTime, endPeriod);

    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  };

  // Get dynamic status for display
  const getDoctorDisplayStatus = (doctor: Doctor): { status: string; color: string } => {
    if (doctor.status === 'On Leave') {
      return { status: 'On Leave', color: '#F59E0B' };
    }
    if (doctor.status === 'Inactive') {
      return { status: 'Inactive', color: '#9CA3AF' };
    }
    
    const isAvailable = isDoctorCurrentlyAvailable(doctor);
    return {
      status: isAvailable ? 'Available Now' : 'Off Duty',
      color: isAvailable ? '#10B981' : '#EF4444'
    };
  };

  // Utility functions
  const resetForm = () => {
    setDoctorForm({
      name: '',
      credentials: '',
      specialization: '',
      department: '',
      customDepartment: '',
      fellowship: '',
      experience: '',
      rating: 4.5,
      consultationFee: '',
      phone: '',
      email: '',
      availability: '6 days/week',
      schedule: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      timeSlots: '9:00 AM - 6:00 PM',
      expertise: [''],
      avatar: '',
      profileImage: null,
      qualifications: [''],
      status: 'Active'
    });
  };

  // Image upload handler with Firebase Storage
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log('🎯 DOCTOR IMAGE UPLOAD STARTED');

    // Validate file type
    if (!file.type.startsWith('image/')) {
      const msg = 'Please select an image file (JPG, PNG, etc.)';
      console.error('❌', msg);
      setSnackbar({ open: true, message: msg, severity: 'error' });
      return;
    }

    // Validate file size (max 5MB for cloud storage)
    if (file.size > 5 * 1024 * 1024) {
      const msg = 'Image size must be less than 5MB';
      console.error('❌', msg);
      setSnackbar({ open: true, message: msg, severity: 'error' });
      return;
    }

    try {
      setUploadingImage(true);
      console.log('📤 Uploading doctor image to Firebase Storage...');
      console.log('📦 File:', file.name, '| Size:', (file.size / 1024).toFixed(2), 'KB');
      
      // Generate temporary doctor ID for upload (will be replaced with actual ID after save)
      const tempDoctorId = `temp_${Date.now()}`;
      
      // Upload to Firebase Storage
      const cloudImageUrl = await DoctorImageService.uploadDoctorImage(file, tempDoctorId);
      
      console.log('✅✅✅ CLOUD IMAGE URL READY:', cloudImageUrl);
      console.log('🔍 Is cloud URL?', DoctorImageService.isCloudImageUrl(cloudImageUrl));
      
      // Update form with cloud URL
      setDoctorForm(prev => ({ ...prev, avatar: cloudImageUrl }));
      console.log('✅ Form updated with cloud image URL');
      
      setSnackbar({ 
        open: true, 
        message: 'Image uploaded to cloud! Will sync across all devices.', 
        severity: 'success' 
      });
      
    } catch (error: any) {
      console.error('❌ IMAGE UPLOAD FAILED:', error);
      
      setSnackbar({ 
        open: true, 
        message: `Failed to upload image: ${error.message}`, 
        severity: 'error' 
      });
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  // Compress image for faster upload
  const compressImage = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            
            // Resize if image is too large (max 800x800)
            const maxSize = 800;
            if (width > maxSize || height > maxSize) {
              if (width > height) {
                height = (height / width) * maxSize;
                width = maxSize;
              } else {
                width = (width / height) * maxSize;
                height = maxSize;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              reject(new Error('Could not get canvas context'));
              return;
            }
            
            ctx.drawImage(img, 0, 0, width, height);
            
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  const compressedFile = new File([blob], file.name, {
                    type: 'image/jpeg',
                    lastModified: Date.now(),
                  });
                  resolve(compressedFile);
                } else {
                  reject(new Error('Canvas toBlob failed'));
                }
              },
              'image/jpeg',
              0.85 // Quality: 85%
            );
          } catch (error) {
            reject(error);
          }
        };
        img.onerror = () => reject(new Error('Failed to load image'));
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  };

  const handleScheduleChange = (day: string) => {
    setDoctorForm(prev => ({
      ...prev,
      schedule: prev.schedule.includes(day)
        ? prev.schedule.filter(d => d !== day)
        : [...prev.schedule, day]
    }));
  };

  const addExpertise = () => {
    setDoctorForm(prev => ({
      ...prev,
      expertise: [...prev.expertise, '']
    }));
  };

  const updateExpertise = (index: number, value: string) => {
    setDoctorForm(prev => ({
      ...prev,
      expertise: prev.expertise.map((exp, i) => i === index ? value : exp)
    }));
  };

  const removeExpertise = (index: number) => {
    setDoctorForm(prev => ({
      ...prev,
      expertise: prev.expertise.filter((_, i) => i !== index)
    }));
  };

  const addQualification = () => {
    setDoctorForm(prev => ({
      ...prev,
      qualifications: [...prev.qualifications, '']
    }));
  };

  const updateQualification = (index: number, value: string) => {
    setDoctorForm(prev => ({
      ...prev,
      qualifications: prev.qualifications.map((qual, i) => i === index ? value : qual)
    }));
  };

  const removeQualification = (index: number) => {
    setDoctorForm(prev => ({
      ...prev,
      qualifications: prev.qualifications.filter((_, i) => i !== index)
    }));
  };

  // Handler functions
  const handleAddDoctor = async () => {
    const finalDepartment = doctorForm.department === 'Other' ? doctorForm.customDepartment : doctorForm.department;
    
    if (!doctorForm.name || !doctorForm.credentials || !doctorForm.specialization || !finalDepartment) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }

    if (doctorForm.department === 'Other' && !doctorForm.customDepartment.trim()) {
      setSnackbar({ open: true, message: 'Please specify the custom department', severity: 'error' });
      return;
    }

    // Validate phone number - must be exactly 10 digits
    if (doctorForm.phone && doctorForm.phone.length !== 10) {
      setSnackbar({ open: true, message: 'Phone number must be exactly 10 digits', severity: 'error' });
      return;
    }
    if (doctorForm.phone && !/^[6-9]\d{9}$/.test(doctorForm.phone)) {
      setSnackbar({ open: true, message: 'Invalid phone number format', severity: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const finalDepartment = doctorForm.department === 'Other' ? doctorForm.customDepartment : doctorForm.department;
      
      const doctorData = {
        ...doctorForm,
        department: finalDepartment,
        consultationFee: parseInt(doctorForm.consultationFee) || 500,
        expertise: doctorForm.expertise.filter(exp => exp.trim() !== ''),
        qualifications: doctorForm.qualifications.filter(qual => qual.trim() !== ''),
        avatar: doctorForm.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(doctorForm.name)}&background=4AA3DF&color=fff`
      };
      
      // Remove customDepartment and profileImage from the data sent to Firebase
      delete (doctorData as any).customDepartment;
      delete (doctorData as any).profileImage;
      
      console.log('📤 Saving doctor with avatar:', doctorData.avatar);
      
      await FirebaseDoctorService.addDoctor(doctorData);
      setShowAddModal(false);
      resetForm();
      
      setSnackbar({ open: true, message: 'Doctor added successfully! Image will appear in mobile app.', severity: 'success' });
    } catch (error) {
      console.error('Error adding doctor:', error);
      setSnackbar({ open: true, message: 'Failed to add doctor', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditDoctor = (doctor: Doctor) => {
    setSelectedDoctor(doctor);
    const isOtherDepartment = !departmentOptionsForForm.filter(d => d !== 'Other').includes(doctor.department);
    setDoctorForm({
      name: doctor.name,
      credentials: doctor.credentials,
      specialization: doctor.specialization,
      department: isOtherDepartment ? 'Other' : doctor.department,
      customDepartment: isOtherDepartment ? doctor.department : '',
      fellowship: doctor.fellowship || '',
      experience: doctor.experience,
      rating: doctor.rating,
      consultationFee: doctor.consultationFee.toString(),
      phone: doctor.phone,
      email: doctor.email,
      availability: doctor.availability,
      schedule: doctor.schedule,
      timeSlots: doctor.timeSlots,
      expertise: doctor.expertise,
      avatar: doctor.avatar,
      profileImage: null,
      qualifications: doctor.qualifications,
      status: doctor.status
    });
    setShowEditModal(true);
  };

  const handleUpdateDoctor = async () => {
    if (!selectedDoctor) return;

    if (doctorForm.department === 'Other' && !doctorForm.customDepartment.trim()) {
      setSnackbar({ open: true, message: 'Please specify the custom department', severity: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const finalDepartment = doctorForm.department === 'Other' ? doctorForm.customDepartment : doctorForm.department;
      
      // Parse consultation fee, keep the actual value entered
      const parsedFee = parseInt(doctorForm.consultationFee);
      if (isNaN(parsedFee) || parsedFee < 0) {
        setSnackbar({ open: true, message: 'Please enter a valid consultation fee', severity: 'error' });
        setIsLoading(false);
        return;
      }
      
      const updateData = {
        ...doctorForm,
        department: finalDepartment,
        consultationFee: parsedFee,
        expertise: doctorForm.expertise.filter(exp => exp.trim() !== ''),
        qualifications: doctorForm.qualifications.filter(qual => qual.trim() !== ''),
        status: doctorForm.status,
        isActive: doctorForm.status === 'Active',
        updatedAt: new Date().toISOString()
      };
      
      // Remove customDepartment and profileImage from the data sent to Firebase
      delete (updateData as any).customDepartment;
      delete (updateData as any).profileImage;
      
      console.log('📤 Updating doctor with data:', updateData);
      console.log('💰 Consultation Fee being updated:', parsedFee);
      console.log('📤 Doctor ID:', selectedDoctor.id);
      
      await FirebaseDoctorService.updateDoctor(selectedDoctor.id, updateData);
      setShowEditModal(false);
      resetForm();
      setSelectedDoctor(null);
      
      setSnackbar({ open: true, message: 'Doctor updated successfully! Changes will reflect in mobile app.', severity: 'success' });
    } catch (error) {
      console.error('Error updating doctor:', error);
      setSnackbar({ open: true, message: 'Failed to update doctor', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDoctor = (doctor: Doctor) => {
    setSelectedDoctor(doctor);
    setShowViewModal(true);
  };

  const handleDeleteDoctor = async (doctorId: string) => {
    const doctor = doctors.find(d => d.id === doctorId);
    if (!doctor) {
      setSnackbar({ open: true, message: 'Doctor not found', severity: 'error' });
      return;
    }
    
    if (window.confirm(`Are you sure you want to permanently delete Dr. ${doctor.name}? This action cannot be undone.`)) {
      try {
        console.log('🗑️ Deleting doctor:', doctorId);
        const result = await FirebaseDoctorService.deleteDoctor(doctorId);
        
        if (result && result.success) {
          // Remove doctor from local state immediately for better UX
          setDoctors(prevDoctors => prevDoctors.filter(d => d.id !== doctorId));
          setSnackbar({ open: true, message: `Dr. ${doctor.name} has been permanently deleted`, severity: 'success' });
          console.log('✅ Doctor deleted successfully from admin dashboard');
        } else {
          throw new Error(result?.message || 'Failed to delete doctor');
        }
      } catch (error: any) {
        console.error('❌ Error deleting doctor:', error);
        setSnackbar({ open: true, message: `Failed to delete doctor: ${error.message || 'Unknown error'}`, severity: 'error' });
      }
    }
  };

  // Filter menu handlers
  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterSelect = (department: string) => {
    setSelectedDepartment(department);
    setFilterAnchorEl(null);
  };

  // Component renders
  const StatCard: React.FC<{ title: string; value: string | number; color: string; icon: React.ReactNode; subtitle?: string }> = ({ 
    title, value, color, icon, subtitle 
  }) => (
    <Card sx={{ 
      backgroundColor: color,
      color: 'white',
      borderRadius: 3,
      minHeight: 120
    }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.2)', 
            borderRadius: 2, 
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {icon}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: '24px', lineHeight: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ fontSize: '12px', opacity: 0.8, mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '14px' }}>
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  const DoctorCard: React.FC<{ doctor: Doctor }> = ({ doctor }) => {
    const dynamicStatus = getDoctorDisplayStatus(doctor);
    
    return (
    <Card sx={{ mb: 2, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <CardContent sx={{ p: 3 }}>
        {/* Doctor Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar 
              src={doctor.avatar} 
              sx={{ width: 60, height: 60 }}
            >
              {doctor.name.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '18px' }}>
                {doctor.name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
                {doctor.credentials}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Rating value={doctor.rating} readOnly size="small" />
                <Typography variant="body2" sx={{ color: '#666' }}>
                  ({doctor.rating})
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'flex-end' }}>
            <Chip 
              label={dynamicStatus.status} 
              sx={{ backgroundColor: dynamicStatus.color, color: 'white', fontWeight: 'bold' }}
              size="small"
            />
            <Chip 
              label={doctor.specialization} 
              sx={{ backgroundColor: '#E8F5E8', color: '#2E7D32', fontWeight: 'bold' }}
              size="small"
            />
          </Box>
        </Box>
        
        {/* Doctor Details */}
        <Box sx={{ mb: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 'bold' }}>Department</Typography>
            <Typography variant="body2">{doctor.department}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 'bold' }}>Experience</Typography>
            <Typography variant="body2">{doctor.experience}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 'bold' }}>Consultation Fee</Typography>
            <Typography variant="body2">₹{doctor.consultationFee}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 'bold' }}>Today's Appointments</Typography>
            <Typography variant="body2">{doctor.todayAppointments || 0}</Typography>
          </Box>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
            <PhoneIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
            {doctor.phone}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
            <EmailIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
            {doctor.email}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            <ScheduleIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
            {doctor.timeSlots} • {doctor.availability}
          </Typography>
        </Box>

        {/* Expertise Tags */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 'bold', mb: 1 }}>
            Expertise
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {doctor.expertise.slice(0, 3).map((exp, index) => (
              <Chip key={index} label={exp} size="small" variant="outlined" />
            ))}
            {doctor.expertise.length > 3 && (
              <Chip label={`+${doctor.expertise.length - 3} more`} size="small" variant="outlined" />
            )}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="text"
            size="small"
            startIcon={<ViewIcon />}
            onClick={() => handleViewDoctor(doctor)}
            sx={{ color: '#007AFF' }}
          >
            View
          </Button>
          <Button
            variant="text"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => handleEditDoctor(doctor)}
            sx={{ color: '#8A2BE2' }}
          >
            Edit
          </Button>
          <Button
            variant="text"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => handleDeleteDoctor(doctor.id)}
            sx={{ color: '#EF4444' }}
          >
            Remove
          </Button>
        </Box>
      </CardContent>
    </Card>
    );
  };

  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1A202C', mb: 1 }}>
          Doctor Management
        </Typography>
        <Typography variant="body1" sx={{ color: '#718096' }}>
          Manage doctor profiles and schedules
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
        gap: 3,
        mb: 4 
      }}>
        <StatCard 
          title="Total Doctors" 
          value={doctorStats.totalDoctors} 
          color="#4A90E2" 
          icon={<GroupIcon />}
          subtitle="All registered doctors"
        />
        <StatCard 
          title="Active Doctors" 
          value={doctorStats.activeDoctors} 
          color="#10B981" 
          icon={<PersonIcon />}
          subtitle="Currently available"
        />
        <StatCard 
          title="Off Duty" 
          value={doctorStats.offDuty} 
          color="#EF4444" 
          icon={<HospitalIcon />}
          subtitle="Not available now"
        />
      </Box>

      {/* Search and Filter */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search doctors by name, specialization, department..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: '#9CA3AF' }} />,
          }}
          sx={{ flex: 1 }}
        />
        
        <Button
          variant="outlined"
          startIcon={<FilterIcon />}
          onClick={handleFilterClick}
          sx={{ minWidth: 180 }}
        >
          {selectedDepartment}
        </Button>
        
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterClose}
        >
          {mockDepartments.map((department) => (
            <MenuItem 
              key={department} 
              onClick={() => handleFilterSelect(department)}
            >
              {department}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddModal(true)}
          sx={{ 
            backgroundColor: '#10B981', 
            '&:hover': { backgroundColor: '#059669' },
            borderRadius: 3
          }}
        >
          Add Doctor
        </Button>
        <Button
          variant="outlined"
          startIcon={isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      {/* Doctors List */}
      <Box>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredDoctors.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: '#666', mb: 2 }}>
              No doctors found
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              {searchQuery || selectedDepartment !== 'All' 
                ? 'Try adjusting your search or filter criteria'
                : 'No doctors have been added yet'
              }
            </Typography>
          </Card>
        ) : (
          filteredDoctors.map((doctor) => (
            <DoctorCard key={doctor.id} doctor={doctor} />
          ))
        )}
      </Box>

      {/* Add/Edit Doctor Modal */}
      <Dialog
        open={showAddModal || showEditModal}
        onClose={() => {
          setShowAddModal(false);
          setShowEditModal(false);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{showEditModal ? 'Edit Doctor' : 'Add New Doctor'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Profile Image Upload */}
            <Typography variant="h6" sx={{ color: '#666' }}>Profile Image</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
              <Avatar 
                src={doctorForm.avatar} 
                sx={{ width: 120, height: 120, border: '3px solid #e0e0e0' }}
              >
                {doctorForm.name ? doctorForm.name.charAt(0) : <DefaultAvatarIcon sx={{ fontSize: 60 }} />}
              </Avatar>
              {uploadingImage && (
                <Box sx={{ width: '100%', textAlign: 'center' }}>
                  <CircularProgress size={24} sx={{ mb: 1 }} />
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Compressing and uploading...
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={uploadingImage ? <CircularProgress size={20} /> : <CameraIcon />}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? 'Uploading...' : 'Upload Image'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
                {doctorForm.avatar && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setDoctorForm({ ...doctorForm, avatar: '' })}
                    disabled={uploadingImage}
                  >
                    Remove
                  </Button>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: '#666', textAlign: 'center' }}>
                Recommended: Square image, max 5MB (auto-compressed to 800x800)
                <br />
                Image will be visible in patient mobile app immediately
              </Typography>
            </Box>

            <Divider />

            {/* Basic Information */}
            <Typography variant="h6" sx={{ color: '#666' }}>Basic Information</Typography>
            
            {/* Profile Image Upload */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar 
                src={doctorForm.avatar || (doctorForm.profileImage ? URL.createObjectURL(doctorForm.profileImage) : '')}
                sx={{ width: 80, height: 80 }}
              >
                {doctorForm.name ? doctorForm.name.charAt(0) : <DefaultAvatarIcon sx={{ fontSize: 40 }} />}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CameraIcon />}
                  sx={{ mb: 1 }}
                >
                  Upload Profile Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setDoctorForm({ ...doctorForm, profileImage: file });
                      }
                    }}
                  />
                </Button>
                <TextField
                  fullWidth
                  label="Or enter image URL"
                  value={doctorForm.avatar}
                  onChange={(e) => setDoctorForm({ ...doctorForm, avatar: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  size="small"
                />
              </Box>
            </Box>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
              gap: 2 
            }}>
              <TextField
                fullWidth
                label="Doctor Name *"
                value={doctorForm.name}
                onChange={(e) => setDoctorForm({ ...doctorForm, name: e.target.value })}
              />
              <TextField
                fullWidth
                label="Credentials *"
                value={doctorForm.credentials}
                onChange={(e) => setDoctorForm({ ...doctorForm, credentials: e.target.value })}
                placeholder="e.g., MBBS, MD"
              />
              <TextField
                fullWidth
                label="Specialization *"
                value={doctorForm.specialization}
                onChange={(e) => setDoctorForm({ ...doctorForm, specialization: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel id="department-label">Department *</InputLabel>
                <Select
                  labelId="department-label"
                  value={doctorForm.department}
                  label="Department *"
                  onChange={(e) => setDoctorForm({ ...doctorForm, department: e.target.value, customDepartment: '' })}
                >
                  {departmentOptionsForForm.map((dept) => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {doctorForm.department === 'Other' && (
                <TextField
                  fullWidth
                  label="Custom Department *"
                  value={doctorForm.customDepartment}
                  onChange={(e) => setDoctorForm({ ...doctorForm, customDepartment: e.target.value })}
                  placeholder="Enter department name"
                  sx={{ gridColumn: { sm: 'span 2' } }}
                />
              )}
              <TextField
                fullWidth
                label="Fellowship"
                value={doctorForm.fellowship}
                onChange={(e) => setDoctorForm({ ...doctorForm, fellowship: e.target.value })}
              />
              <TextField
                fullWidth
                label="Experience"
                value={doctorForm.experience}
                onChange={(e) => setDoctorForm({ ...doctorForm, experience: e.target.value })}
                placeholder="e.g., 10 years"
              />
            </Box>

            {/* Contact Information */}
            <Typography variant="h6" sx={{ color: '#666' }}>Contact Information</Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
              gap: 2 
            }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={doctorForm.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                  if (value.length <= 10) {
                    setDoctorForm({ ...doctorForm, phone: value });
                  }
                }}
                placeholder="10 digit number"
                inputProps={{ maxLength: 10 }}
                helperText={doctorForm.phone && doctorForm.phone.length !== 10 && doctorForm.phone.length > 0 ? "Must be exactly 10 digits" : "Enter exactly 10 digits"}
                error={doctorForm.phone.length > 0 && doctorForm.phone.length !== 10}
              />
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={doctorForm.email}
                onChange={(e) => setDoctorForm({ ...doctorForm, email: e.target.value })}
              />
            </Box>

            {/* Professional Details */}
            <Typography variant="h6" sx={{ color: '#666' }}>Professional Details</Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, 
              gap: 2 
            }}>
              <TextField
                fullWidth
                label="Consultation Fee"
                type="number"
                value={doctorForm.consultationFee}
                onChange={(e) => setDoctorForm({ ...doctorForm, consultationFee: e.target.value })}
                InputProps={{
                  startAdornment: '₹'
                }}
              />
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>Rating</Typography>
                <Rating
                  value={doctorForm.rating}
                  onChange={(event, newValue) => {
                    setDoctorForm({ ...doctorForm, rating: newValue || 0 });
                  }}
                  precision={0.5}
                />
              </Box>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={doctorForm.status}
                  onChange={(e) => setDoctorForm({ ...doctorForm, status: e.target.value as any })}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="On Leave">On Leave</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Schedule */}
            <Typography variant="h6" sx={{ color: '#666' }}>Schedule</Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
              gap: 2,
              mb: 2
            }}>
              <TextField
                fullWidth
                label="Time Slots"
                value={doctorForm.timeSlots}
                onChange={(e) => setDoctorForm({ ...doctorForm, timeSlots: e.target.value })}
              />
              <TextField
                fullWidth
                label="Availability"
                value={doctorForm.availability}
                onChange={(e) => setDoctorForm({ ...doctorForm, availability: e.target.value })}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Working Days</Typography>
              <FormGroup row>
                {daysOfWeek.map((day) => (
                  <FormControlLabel
                    key={day}
                    control={
                      <Checkbox
                        checked={doctorForm.schedule.includes(day)}
                        onChange={() => handleScheduleChange(day)}
                      />
                    }
                    label={day}
                  />
                ))}
              </FormGroup>
            </Box>

            {/* Expertise */}
            <Typography variant="h6" sx={{ color: '#666' }}>Areas of Expertise</Typography>
            {doctorForm.expertise.map((exp, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label={`Expertise ${index + 1}`}
                  value={exp}
                  onChange={(e) => updateExpertise(index, e.target.value)}
                />
                <IconButton onClick={() => removeExpertise(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button onClick={addExpertise} startIcon={<AddIcon />} variant="outlined">
              Add Expertise
            </Button>

            {/* Qualifications */}
            <Typography variant="h6" sx={{ color: '#666' }}>Qualifications</Typography>
            {doctorForm.qualifications.map((qual, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label={`Qualification ${index + 1}`}
                  value={qual}
                  onChange={(e) => updateQualification(index, e.target.value)}
                />
                <IconButton onClick={() => removeQualification(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button onClick={addQualification} startIcon={<AddIcon />} variant="outlined">
              Add Qualification
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowAddModal(false);
            setShowEditModal(false);
            resetForm();
          }}>
            Cancel
          </Button>
          <Button 
            onClick={showEditModal ? handleUpdateDoctor : handleAddDoctor} 
            variant="contained" 
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : showEditModal ? 'Update Doctor' : 'Add Doctor'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Doctor Modal */}
      <Dialog
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Doctor Details</DialogTitle>
        <DialogContent>
          {selectedDoctor && (
            <Box sx={{ pt: 2 }}>
              {/* Doctor Profile Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}>
                <Avatar 
                  src={selectedDoctor.avatar} 
                  sx={{ width: 100, height: 100 }}
                >
                  {selectedDoctor.name.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {selectedDoctor.name}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                    {selectedDoctor.credentials}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Rating value={selectedDoctor.rating} readOnly />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      ({selectedDoctor.rating}/5.0)
                    </Typography>
                  </Box>
                  <Chip 
                    label={selectedDoctor.status} 
                    sx={{ backgroundColor: selectedDoctor.statusColor, color: 'white' }}
                  />
                </Box>
              </Box>

              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                gap: 3 
              }}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>Professional Information</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Specialization:</strong> {selectedDoctor.specialization}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Department:</strong> {selectedDoctor.department}
                  </Typography>
                  {selectedDoctor.fellowship && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Fellowship:</strong> {selectedDoctor.fellowship}
                    </Typography>
                  )}
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Experience:</strong> {selectedDoctor.experience}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Consultation Fee:</strong> ₹{selectedDoctor.consultationFee}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>Contact Information</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Phone:</strong> {selectedDoctor.phone}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Email:</strong> {selectedDoctor.email}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Availability:</strong> {selectedDoctor.availability}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Time Slots:</strong> {selectedDoctor.timeSlots}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Schedule</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  {selectedDoctor.schedule.map((day) => (
                    <Chip key={day} label={day} variant="outlined" />
                  ))}
                </Box>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Areas of Expertise</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {selectedDoctor.expertise.map((exp, index) => (
                    <Chip key={index} label={exp} color="primary" variant="outlined" />
                  ))}
                </Box>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Qualifications</Typography>
                {selectedDoctor.qualifications.map((qual, index) => (
                  <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                    • {qual}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowViewModal(false)}>Close</Button>
          {selectedDoctor && (
            <Button onClick={() => handleEditDoctor(selectedDoctor)} variant="contained">
              Edit Doctor
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Doctors;